import { db } from './firebase';
import { collection, addDoc, query, orderBy, limit, getDocs, where, Timestamp } from 'firebase/firestore';

export interface Activity {
  id: string;
  type: 'like' | 'comment' | 'follow' | 'post' | 'token_discovery';
  actorId: string; // ID de l'utilisateur qui fait l'action
  targetId?: string; // ID de l'utilisateur cible (pour follow)
  postId?: string; // ID du post concerné
  tokenSymbol?: string; // Symbole du token (pour token_discovery)
  content?: string; // Contenu optionnel
  createdAt: Date;
  actorUsername?: string; // Cache du nom d'utilisateur pour éviter des requêtes
  actorDisplayName?: string;
}

export class ActivityService {
  // Créer une nouvelle activité
  static async createActivity(activity: Omit<Activity, 'id' | 'createdAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'activities'), {
        ...activity,
        createdAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error: any) {
      console.error('Erreur création activité:', error);
      throw error;
    }
  }

  // Récupérer les activités récentes pour un utilisateur
  static async getRecentActivities(userId: string, limitCount: number = 10): Promise<Activity[]> {
    try {
      // Récupérer les activités où l'utilisateur est la cible ou acteur
      const q = query(
        collection(db, 'activities'),
        where('targetId', '==', userId), // Activités qui concernent cet utilisateur
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const activities: Activity[] = [];

      querySnapshot.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate()
        } as Activity);
      });

      return activities;
    } catch (error) {
      console.error('Erreur récupération activités:', error);
      return [];
    }
  }

  // Récupérer les activités globales récentes (pour le feed public)
  static async getGlobalRecentActivities(limitCount: number = 20): Promise<Activity[]> {
    try {
      const q = query(
        collection(db, 'activities'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const activities: Activity[] = [];

      querySnapshot.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate()
        } as Activity);
      });

      return activities;
    } catch (error) {
      console.error('Erreur récupération activités globales:', error);
      // En cas d'erreur, retourner des données de démonstration
      return [
        {
          id: 'demo-1',
          type: 'token_discovery',
          actorId: 'demo-user',
          tokenSymbol: 'JUP',
          actorUsername: 'jupipotes_bot',
          actorDisplayName: 'Jupipotes Bot 🤖',
          createdAt: new Date(Date.now() - 1000 * 60 * 5) // Il y a 5 minutes
        },
        {
          id: 'demo-2',
          type: 'like',
          actorId: 'demo-user-2',
          postId: 'demo-post',
          actorUsername: 'space_explorer',
          actorDisplayName: 'Space Explorer 🌌',
          createdAt: new Date(Date.now() - 1000 * 60 * 15) // Il y a 15 minutes
        },
        {
          id: 'demo-3',
          type: 'follow',
          actorId: 'demo-user-3',
          targetId: 'current-user',
          actorUsername: 'crypto_trader',
          actorDisplayName: 'Crypto Trader ⭐',
          createdAt: new Date(Date.now() - 1000 * 60 * 30) // Il y a 30 minutes
        }
      ];
    }
  }

  // Enregistrer un like sur un post
  static async recordLikeActivity(actorId: string, postId: string, actorUsername: string, actorDisplayName: string): Promise<void> {
    try {
      await this.createActivity({
        type: 'like',
        actorId,
        postId,
        actorUsername,
        actorDisplayName
      });
    } catch (error) {
      console.error('Erreur enregistrement like:', error);
    }
  }

  // Enregistrer un commentaire sur un post
  static async recordCommentActivity(actorId: string, postId: string, content: string, actorUsername: string, actorDisplayName: string): Promise<void> {
    try {
      await this.createActivity({
        type: 'comment',
        actorId,
        postId,
        content: content.substring(0, 100), // Limiter la longueur
        actorUsername,
        actorDisplayName
      });
    } catch (error) {
      console.error('Erreur enregistrement commentaire:', error);
    }
  }

  // Enregistrer un follow
  static async recordFollowActivity(actorId: string, targetId: string, actorUsername: string, actorDisplayName: string): Promise<void> {
    try {
      await this.createActivity({
        type: 'follow',
        actorId,
        targetId,
        actorUsername,
        actorDisplayName
      });
    } catch (error) {
      console.error('Erreur enregistrement follow:', error);
    }
  }

  // Enregistrer un nouveau post
  static async recordPostActivity(actorId: string, postId: string, actorUsername: string, actorDisplayName: string): Promise<void> {
    try {
      await this.createActivity({
        type: 'post',
        actorId,
        postId,
        actorUsername,
        actorDisplayName
      });
    } catch (error) {
      console.error('Erreur enregistrement post:', error);
    }
  }

  // Enregistrer la découverte d'un token
  static async recordTokenDiscoveryActivity(actorId: string, tokenSymbol: string, actorUsername: string, actorDisplayName: string): Promise<void> {
    try {
      await this.createActivity({
        type: 'token_discovery',
        actorId,
        tokenSymbol,
        actorUsername,
        actorDisplayName
      });
    } catch (error) {
      console.error('Erreur enregistrement découverte token:', error);
    }
  }
}
